/**
 * Fantasy Console 0 - Pixel Editor Component
 *
 * 8×8 zoomed pixel editor canvas for drawing on individual sprites.
 * Supports multiple drawing tools with preview overlays.
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import { SPRITE_SIZE, SPRITE_SHEET_SIZE, ColorIndex } from '@fc0/types'
import {
  PICO8_RGB,
  getPixel,
  getSpriteCoords,
  getPixelFromEditorPosition,
  floodFill,
  drawLine,
  drawRect,
  getLinePoints,
  getRectPoints,
} from '../../utils/spriteUtils'
import styles from './PixelEditor.module.css'
import editorStyles from '../../styles/editor.module.css'

export type DrawingTool =
  | 'pencil'
  | 'fill'
  | 'eraser'
  | 'picker'
  | 'line'
  | 'rect'

interface PixelEditorProps {
  sprites: Uint8Array
  selectedSprite: number
  selectedColor: ColorIndex
  selectedTool: DrawingTool
  onSpritesChange: (sprites: Uint8Array) => void
  onColorPick?: (color: ColorIndex) => void
  canvasSize?: number
}

interface DragState {
  startX: number
  startY: number
  currentX: number
  currentY: number
}

export function PixelEditor({
  sprites,
  selectedSprite,
  selectedColor,
  selectedTool,
  onSpritesChange,
  onColorPick,
  canvasSize = 192,
}: PixelEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const localSpritesRef = useRef<Uint8Array | null>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const hoverPosRef = useRef<{ x: number; y: number } | null>(null)

  // Store props in refs to access latest values in render without recreating render function
  const spritesRef = useRef(sprites)
  const selectedSpriteRef = useRef(selectedSprite)
  const selectedColorRef = useRef(selectedColor)
  const selectedToolRef = useRef(selectedTool)

  // Update refs when props change
  spritesRef.current = sprites
  selectedSpriteRef.current = selectedSprite
  selectedColorRef.current = selectedColor
  selectedToolRef.current = selectedTool

  const pixelSize = canvasSize / SPRITE_SIZE

  // Stable render function that reads from refs
  const render = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const currentSprites = localSpritesRef.current || spritesRef.current
    const spriteCoords = getSpriteCoords(selectedSpriteRef.current)

    canvas.width = canvasSize
    canvas.height = canvasSize

    // Draw pixels
    for (let py = 0; py < SPRITE_SIZE; py++) {
      for (let px = 0; px < SPRITE_SIZE; px++) {
        const colorIndex = getPixel(
          currentSprites,
          spriteCoords.x + px,
          spriteCoords.y + py,
        )
        const [r, g, b] = PICO8_RGB[colorIndex]
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
        ctx.fillRect(px * pixelSize, py * pixelSize, pixelSize, pixelSize)
      }
    }

    // Draw preview for line/rect tools during drag
    const dragState = dragStateRef.current
    const tool = selectedToolRef.current
    if (dragState && (tool === 'line' || tool === 'rect')) {
      const [r, g, b] = PICO8_RGB[selectedColorRef.current]
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.5)`

      const points =
        tool === 'line'
          ? getLinePoints(
              dragState.startX,
              dragState.startY,
              dragState.currentX,
              dragState.currentY,
            )
          : getRectPoints(
              dragState.startX,
              dragState.startY,
              dragState.currentX,
              dragState.currentY,
              true,
            )

      for (const [px, py] of points) {
        if (px >= 0 && px < SPRITE_SIZE && py >= 0 && py < SPRITE_SIZE) {
          ctx.fillRect(
            px * pixelSize + 1,
            py * pixelSize + 1,
            pixelSize - 2,
            pixelSize - 2,
          )
        }
      }
    }

    // Draw hover indicator (only when not drawing)
    const hoverPos = hoverPosRef.current
    if (hoverPos && !isDrawingRef.current) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)'
      ctx.lineWidth = 2
      ctx.strokeRect(
        hoverPos.x * pixelSize + 2,
        hoverPos.y * pixelSize + 2,
        pixelSize - 4,
        pixelSize - 4,
      )
    }
  }, [canvasSize, pixelSize])

  // Re-render when sprites or selectedSprite prop changes (only if not drawing)
  useEffect(() => {
    if (!isDrawingRef.current) {
      render()
    }
  }, [sprites, selectedSprite, render])

  // Get pixel coords from mouse event
  const getPixelCoords = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      if (!canvas) return null

      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      return getPixelFromEditorPosition(
        x,
        y,
        canvasSize,
        selectedSpriteRef.current,
      )
    },
    [canvasSize],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      if (e.button !== 0) return // Only draw on left-click
      const coords = getPixelCoords(e)
      if (!coords) return

      const tool = selectedToolRef.current
      const color = selectedColorRef.current
      const currentSprites = spritesRef.current
      const spriteCoords = getSpriteCoords(selectedSpriteRef.current)

      isDrawingRef.current = true

      if (tool === 'line' || tool === 'rect') {
        dragStateRef.current = {
          startX: coords.x,
          startY: coords.y,
          currentX: coords.x,
          currentY: coords.y,
        }
        render()
      } else if (tool === 'pencil' || tool === 'eraser') {
        localSpritesRef.current = new Uint8Array(currentSprites)
        const drawColor = tool === 'eraser' ? 0 : color
        localSpritesRef.current[
          coords.sheetY * SPRITE_SHEET_SIZE + coords.sheetX
        ] = drawColor
        render()
      } else if (tool === 'fill') {
        const bounds = {
          minX: spriteCoords.x,
          minY: spriteCoords.y,
          maxX: spriteCoords.x + SPRITE_SIZE - 1,
          maxY: spriteCoords.y + SPRITE_SIZE - 1,
        }
        const newSprites = floodFill(
          currentSprites,
          coords.sheetX,
          coords.sheetY,
          color,
          bounds,
        )
        isDrawingRef.current = false
        onSpritesChange(newSprites)
      } else if (tool === 'picker') {
        const pickedColor = getPixel(
          currentSprites,
          coords.sheetX,
          coords.sheetY,
        )
        isDrawingRef.current = false
        onColorPick?.(pickedColor)
      }
    },
    [getPixelCoords, onSpritesChange, onColorPick, render],
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const coords = getPixelCoords(e)

      if (!coords) {
        hoverPosRef.current = null
        if (!isDrawingRef.current) render()
        return
      }

      hoverPosRef.current = { x: coords.x, y: coords.y }

      if (!isDrawingRef.current) {
        render()
        return
      }

      if (dragStateRef.current) {
        dragStateRef.current.currentX = coords.x
        dragStateRef.current.currentY = coords.y
        render()
      } else if (localSpritesRef.current) {
        const tool = selectedToolRef.current
        if (tool === 'pencil' || tool === 'eraser') {
          const color = tool === 'eraser' ? 0 : selectedColorRef.current
          localSpritesRef.current[
            coords.sheetY * SPRITE_SHEET_SIZE + coords.sheetX
          ] = color
          render()
        }
      }
    },
    [getPixelCoords, render],
  )

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return

      const coords = getPixelCoords(e)
      const tool = selectedToolRef.current
      const color = selectedColorRef.current
      const currentSprites = spritesRef.current
      const spriteCoords = getSpriteCoords(selectedSpriteRef.current)

      if (dragStateRef.current && coords) {
        if (tool === 'line') {
          const newSprites = drawLine(
            currentSprites,
            spriteCoords.x + dragStateRef.current.startX,
            spriteCoords.y + dragStateRef.current.startY,
            spriteCoords.x + coords.x,
            spriteCoords.y + coords.y,
            color,
          )
          dragStateRef.current = null
          isDrawingRef.current = false
          onSpritesChange(newSprites)
        } else if (tool === 'rect') {
          const filled = !e.shiftKey
          const newSprites = drawRect(
            currentSprites,
            spriteCoords.x + dragStateRef.current.startX,
            spriteCoords.y + dragStateRef.current.startY,
            spriteCoords.x + coords.x,
            spriteCoords.y + coords.y,
            color,
            filled,
          )
          dragStateRef.current = null
          isDrawingRef.current = false
          onSpritesChange(newSprites)
        }
      } else if (localSpritesRef.current) {
        const spritesToCommit = localSpritesRef.current
        localSpritesRef.current = null
        isDrawingRef.current = false
        onSpritesChange(spritesToCommit)
      } else {
        isDrawingRef.current = false
      }
    },
    [getPixelCoords, onSpritesChange],
  )

  const handleMouseLeave = useCallback(() => {
    hoverPosRef.current = null

    if (
      isDrawingRef.current &&
      !dragStateRef.current &&
      localSpritesRef.current
    ) {
      const spritesToCommit = localSpritesRef.current
      localSpritesRef.current = null
      isDrawingRef.current = false
      onSpritesChange(spritesToCommit)
    } else {
      render()
    }
  }, [onSpritesChange, render])

  // Right-click to pick color (like map editor picks tile)
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault()
      const coords = getPixelCoords(e)
      if (!coords) return
      const pickedColor = getPixel(
        spritesRef.current,
        coords.sheetX,
        coords.sheetY,
      )
      onColorPick?.(pickedColor)
    },
    [getPixelCoords, onColorPick],
  )

  const getCursor = () => {
    switch (selectedTool) {
      case 'picker':
        return 'crosshair'
      case 'fill':
        return 'cell'
      default:
        return 'crosshair'
    }
  }

  return (
    <div className={`${editorStyles.panelContainer} ${styles.container}`}>
      <div className={editorStyles.panelHeader}>
        <span className={editorStyles.panelLabel}>Sprite Editor</span>
        <span className={editorStyles.panelInfo}>8×8</span>
      </div>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        style={{ width: canvasSize, height: canvasSize, cursor: getCursor() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
      />
    </div>
  )
}
